import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, ChevronLeft, ChevronRight, X
} from 'lucide-react';

const CalendarComponent = ({ isDarkMode = true }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [viewMode, setViewMode] = useState('month');

  // Mock data
  const mockEvents = [
    {
      id: 1,
      title: "Ice Karkade Campaign Launch",
      type: "campaign",
      startDate: "2024-06-15",
      endDate: "2024-06-30",
      description: "Launch of the new Ice Karkade summer campaign across all platforms",
      assignees: ["Sarah Ahmed", "Mohammed Ali"],
      budget: 25000,
      status: "scheduled",
      priority: "high"
    },
    {
      id: 2,
      title: "Brand Guidelines Review",
      type: "meeting",
      startDate: "2024-06-20",
      endDate: "2024-06-20",
      description: "Review and update brand guidelines for 2024",
      assignees: ["Fatima Hassan"],
      budget: 0,
      status: "scheduled",
      priority: "medium"
    },
    {
      id: 3,
      title: "Product Photography Session",
      type: "production",
      startDate: "2024-06-25",
      endDate: "2024-06-26",
      description: "Photography session for new product line",
      assignees: ["Ahmed Omar", "Mohammed Ali"],
      budget: 5000,
      status: "scheduled",
      priority: "medium"
    },
    {
      id: 4,
      title: "Social Media Content Planning",
      type: "planning",
      startDate: "2024-07-01",
      endDate: "2024-07-05",
      description: "Plan and create content for July social media campaigns",
      assignees: ["Sarah Ahmed", "Fatima Hassan"],
      budget: 0,
      status: "scheduled",
      priority: "low"
    }
  ];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Add empty days for the start of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const getEventsForDate = (date) => {
    if (!date) return [];
    const dateStr = date.toISOString().split('T')[0];
    return mockEvents.filter(event => {
      const eventStart = new Date(event.startDate);
      const eventEnd = new Date(event.endDate);
      const checkDate = new Date(dateStr);
      return checkDate >= eventStart && checkDate <= eventEnd;
    });
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'campaign': return 'bg-purple-100 text-purple-700';
      case 'meeting': return 'bg-blue-100 text-blue-700';
      case 'production': return 'bg-green-100 text-green-700';
      case 'planning': return 'bg-orange-100 text-orange-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-100 text-red-700';
      case 'medium': return 'bg-yellow-100 text-yellow-700';
      case 'low': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  const CalendarDay = ({ date, events }) => {
    const isToday = date && date.toDateString() === new Date().toDateString();
    const isSelected = selectedDate && date && date.toDateString() === selectedDate.toDateString();
    
    return (
      <motion.div
        whileHover={{ scale: 1.02 }}
        className={`min-h-[120px] p-2 border cursor-pointer transition-all duration-200 ${
          isToday 
            ? isDarkMode ? 'bg-blue-500/20 border-blue-400' : 'bg-blue-50 border-blue-300'
            : isSelected 
              ? isDarkMode ? 'bg-purple-500/20 border-purple-400' : 'bg-purple-50 border-purple-300'
              : isDarkMode 
                ? 'border-white/10 hover:bg-white/5' 
                : 'border-gray-200 hover:bg-gray-50'
        }`}
        onClick={() => setSelectedDate(date)}
      >
        <div className="flex justify-between items-start mb-2">
          <span className={`text-sm font-medium ${
            isToday 
              ? isDarkMode ? 'text-blue-300' : 'text-blue-600'
              : isSelected 
                ? isDarkMode ? 'text-purple-300' : 'text-purple-600'
                : isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            {date ? date.getDate() : ''}
          </span>
          {isToday && (
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
          )}
        </div>
        
        <div className="space-y-1">
          {events.slice(0, 2).map(event => (
            <div
              key={event.id}
              className={`text-xs p-1 rounded truncate cursor-pointer ${
                event.priority === 'high' 
                  ? isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-800'
                  : event.priority === 'medium' 
                    ? isDarkMode ? 'bg-yellow-500/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800'
                    : isDarkMode ? 'bg-green-500/20 text-green-300' : 'bg-green-100 text-green-800'
              }`}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEvent(event);
              }}
            >
              {event.title}
            </div>
          ))}
          {events.length > 2 && (
            <div className={`text-xs text-center ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              +{events.length - 2} more
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  const EventCard = ({ event }) => (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className={`backdrop-blur-xl rounded-xl p-3 shadow-lg border transition-all duration-300 ${
        isDarkMode 
          ? 'bg-black/20 border-white/10 hover:shadow-xl hover:bg-black/30' 
          : 'bg-white/80 border-white/20 hover:shadow-xl hover:bg-white/90'
      }`}
    >
      <div className="flex items-start space-x-3">
        <div className={`w-3 h-3 rounded-full mt-1.5 ${getEventTypeColor(event.type)}`}></div>
        <div className="flex-1 min-w-0">
          <h4 className={`font-semibold text-sm mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            {event.title}
          </h4>
          <p className={`text-xs mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {event.time} • {event.duration}
          </p>
          <div className="flex items-center space-x-2">
            <span className={`text-xs px-2 py-1 rounded-full ${getEventTypeColor(event.type)}`}>
              {event.type}
            </span>
            {event.priority === 'high' && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                isDarkMode ? 'bg-red-500/20 text-red-300' : 'bg-red-100 text-red-700'
              }`}>
                High Priority
              </span>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  const EventDetailModal = ({ event, isOpen, onClose }) => {
    if (!event) return null;

    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className={`rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto ${
                isDarkMode ? 'bg-gray-900 border border-white/10' : 'bg-white border border-gray-200'
              }`}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{event.title}</h2>
                  <button
                    onClick={onClose}
                    className={`p-2 rounded-lg transition-colors ${
                      isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-500'
                    }`}
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Left Column - Event Details */}
                  <div className="space-y-6">
                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Event Details</h3>
                      <div className="space-y-3">
                        <div>
                          <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Description</p>
                          <p className={isDarkMode ? 'text-gray-300' : 'text-gray-900'}>{event.description}</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Start Date</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(new Date(event.startDate))}</p>
                          </div>
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>End Date</p>
                            <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formatDate(new Date(event.endDate))}</p>
                          </div>
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Type</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getEventTypeColor(event.type)}`}>
                              {event.type}
                            </span>
                          </div>
                          <div>
                            <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Priority</p>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(event.priority)}`}>
                              {event.priority}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div>
                      <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Team</h3>
                      <div className="space-y-2">
                        {event.assignees.map((assignee, index) => (
                          <div key={index} className={`flex items-center space-x-3 p-2 rounded-lg ${
                            isDarkMode ? 'bg-gray-800' : 'bg-gray-50'
                          }`}>
                            <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                              {assignee.split(' ').map(n => n[0]).join('')}
                            </div>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{assignee}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {event.budget > 0 && (
                      <div>
                        <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Budget</h3>
                        <div className={`p-4 rounded-lg ${
                          isDarkMode ? 'bg-green-500/20' : 'bg-green-50'
                        }`}>
                          <p className={`text-2xl font-bold ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>SAR {event.budget.toLocaleString()}</p>
                          <p className={`text-sm ${isDarkMode ? 'text-green-400' : 'text-green-600'}`}>Allocated budget for this event</p>
                        </div>
                      </div>
                    )}

                    <div className={`flex justify-end space-x-3 pt-6 border-t ${
                      isDarkMode ? 'border-white/10' : 'border-gray-200'
                    }`}>
                      <button className={`px-4 py-2 rounded-lg transition-colors ${
                        isDarkMode 
                          ? 'text-gray-400 hover:text-white hover:bg-white/10' 
                          : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                      }`}>
                        Edit Event
                      </button>
                      <button className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-all duration-300">
                        Share Event
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    );
  };

  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentDate);

  return (
    <div className="w-full h-full px-8 py-8 space-y-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0"
      >
        <div>
          <h1 className="text-4xl font-bold bg-gradient-to-r from-white to-purple-200 bg-clip-text text-transparent">
            Calendar
          </h1>
          <p className="text-gray-300 mt-2 text-lg">
            Schedule and manage your creative projects and events
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          className="px-6 py-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-xl font-medium hover:shadow-lg transition-all duration-300 flex items-center space-x-2"
          onClick={() => setSelectedEvent(null)}
        >
          <Plus className="h-5 w-5" />
          <span>New Event</span>
        </motion.button>
      </motion.div>

      {/* Calendar Navigation */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border ${
          isDarkMode 
            ? 'bg-black/20 border-white/10' 
            : 'bg-white/80 border-gray-200/50'
        }`}
      >
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1))}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <ChevronLeft className="h-5 w-5" />
            </button>
            <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {currentDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
            </h2>
            <button
              onClick={() => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1))}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode ? 'hover:bg-white/10 text-gray-400' : 'hover:bg-gray-100 text-gray-600'
              }`}
            >
              <ChevronRight className="h-5 w-5" />
            </button>
          </div>
          <div className="flex space-x-2">
            {['month', 'week', 'day'].map((mode) => (
              <button
                key={mode}
                onClick={() => setViewMode(mode)}
                className={`px-3 py-1 rounded-lg text-sm font-medium transition-all duration-200 ${
                  viewMode === mode
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
                    : isDarkMode
                      ? 'text-gray-400 hover:text-white hover:bg-white/10'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                }`}
              >
                {mode.charAt(0).toUpperCase() + mode.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Calendar Grid */}
        <div className="grid grid-cols-7 gap-1">
          {/* Weekday Headers */}
          {weekdays.map(day => (
            <div key={day} className={`p-2 text-center text-sm font-medium ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
              {day}
            </div>
          ))}
          
          {/* Calendar Days */}
          {days.map((date, index) => (
            <CalendarDay 
              key={index} 
              date={date} 
              events={getEventsForDate(date)} 
            />
          ))}
        </div>
      </motion.div>

      {/* Selected Date Events */}
      {selectedDate && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className={`backdrop-blur-xl rounded-2xl p-6 shadow-lg border ${
            isDarkMode 
              ? 'bg-black/20 border-white/10' 
              : 'bg-white/80 border-gray-200/50'
          }`}
        >
          <h3 className={`text-lg font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
            Events for {formatDate(selectedDate)}
          </h3>
          <div className="space-y-4">
            {getEventsForDate(selectedDate).map((event) => (
              <EventCard key={event.id} event={event} />
            ))}
            {getEventsForDate(selectedDate).length === 0 && (
              <p className={`text-center py-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>No events scheduled for this date</p>
            )}
          </div>
        </motion.div>
      )}

      {/* Event Detail Modal */}
      <EventDetailModal 
        event={selectedEvent}
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEvent(null)}
      />
    </div>
  );
};

export default CalendarComponent;